import { FileIcon, Upload, X } from 'lucide-react';
import type React from 'react';
import { useRef, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import type { FileData } from '../types/intake.types';

type Props = {
  files: FileData[];
  onChange: (files: FileData[]) => void;
};

async function fileToFileData(file: File): Promise<FileData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, size: file.size, type: file.type, data: reader.result as string });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadBox({ files, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  async function processFiles(fileList: File[]) {
    if (!fileList.length) return;
    setIsLoading(true);
    try {
      const newFileData = await Promise.all(fileList.map(fileToFileData));
      onChange([...files, ...newFileData]);
    } catch (error) {
      console.error('Error converting files:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
    await processFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
      setIsDragging(false);
    }
  }

  async function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    await processFiles(Array.from(e.dataTransfer.files));
  }

  return (
    <div className='w-full space-y-3 pt-4'>
      <input ref={inputRef} type='file' multiple hidden onChange={handleSelect} />

      <div
        onClick={() => !isLoading && inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          w-full border-2 border-dashed rounded-lg px-6 py-8
          flex flex-col items-center justify-center gap-2 text-sm
          transition-colors cursor-pointer
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          ${
            isDragging
              ? 'border-green-500 bg-green-50 text-green-600'
              : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:border-gray-400'
          }
        `}
      >
        <Upload className={`w-6 h-6 ${isDragging ? 'text-green-500' : 'text-gray-400'}`} />
        <span>{isLoading ? 'Processing files...' : isDragging ? 'Drop files here' : 'Click to upload or drag and drop'}</span>
      </div>

      {files.length > 0 && (
        <div className='space-y-2'>
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className='flex items-center justify-between bg-gray-100 border border-gray-200 rounded-md px-3 py-2 text-sm'
            >
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <FileIcon className='w-4 h-4 text-gray-400 shrink-0' />
                <span className='truncate'>{file.name}</span>
              </div>
              <span className='text-xs text-gray-400 mx-3 shrink-0'>{formatFileSize(file.size)}</span>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(files.filter((_, i) => i !== index));
                }}
                className='text-gray-500 hover:text-red-600 hover:bg-transparent p-1 h-auto shrink-0'
              >
                <X className='w-4 h-4' />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
